const SAFE_METHODS = new Set(['get', 'head']);

function hasXsrfHeader(headers) {
  return Object.entries(headers).some(
    ([name, value]) => name.toLowerCase() === 'x-xsrf-token' && Boolean(value),
  );
}

function hasXsrfCookie() {
  return document.cookie.split(';').some((cookie) => {
    const [name, ...value] = cookie.trim().split('=');
    return name === 'XSRF-TOKEN' && value.join('=').length > 0;
  });
}

// Inertia 3 owns its XMLHttpRequest client, so Axios interceptors never see
// page visits. Register the fallback on Inertia's public request hook instead.
export function installInertiaCsrfFallback(
  http,
  { addBreadcrumb, captureMessage },
) {
  return http.onRequest((config) => {
    const method = String(config.method || 'get').toLowerCase();
    const headers = config.headers || {};
    if (SAFE_METHODS.has(method) || hasXsrfHeader(headers) || hasXsrfCookie()) {
      return config;
    }

    const metaToken = document.querySelector(
      'meta[name="csrf-token"]',
    )?.content;
    if (metaToken) {
      addBreadcrumb({
        category: 'csrf',
        message: 'XSRF-TOKEN cookie missing — used meta tag fallback',
        level: 'warning',
        data: { method },
      });
      return {
        ...config,
        headers: { ...headers, 'X-XSRF-TOKEN': metaToken },
      };
    }

    captureMessage(
      'CSRF: XSRF-TOKEN cookie AND meta tag both missing — request will likely 422',
      { level: 'error', extra: { method } },
    );
    return config;
  });
}

// The masked token inertia_rails writes to the XSRF-TOKEN cookie on every
// response.
function xsrfCookieToken() {
  for (const cookie of document.cookie.split(';')) {
    const [name, ...value] = cookie.trim().split('=');
    if (name !== 'XSRF-TOKEN') continue;
    try {
      return decodeURIComponent(value.join('='));
    } catch {
      return null;
    }
  }
  return null;
}

// fetch() callers (lib/api.js and a few direct readers) send the token from
// <meta name="csrf-token">, which only a full page load writes. Signing out
// resets the Rails session and with it the token (#519), so without this every
// fetch write after an in-tab sign-out, including the native app's Google and
// Apple sign-in, fails with 422 until a reload. Inertia's own requests already
// use the cookie.
export function installCsrfMetaSync(router) {
  return router.on('success', () => {
    const token = xsrfCookieToken();
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (token && meta && meta.content !== token) {
      meta.setAttribute('content', token);
    }
  });
}
